/** @jsxImportSource preact */
import { useState, useEffect, useRef } from 'preact/hooks'
import type { JSX } from 'preact'
import { config } from '../config'
import { useFrontContext, extractBookingId, isFrontEmbedded } from '../providers/front'
import { useAuth } from '../providers/auth'

// Define a local type for the request body to avoid importing the Zod schema
type FinancialCompensationBody = {
  'financial-compensation': number
  note: string
}

type ConfirmSubscriptionPausedBody = {
  'subscription-paused': string
  note: string
}

type FormAction = 'record-financial-compensation' | 'confirm-subscription-paused' // extensible for future actions

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

export function CustomerService() {
  const [bookingId, setBookingId] = useState('')
  const [selectedAction, setSelectedAction] = useState<FormAction>('record-financial-compensation')
  const [financialCompensation, setFinancialCompensation] = useState<number | ''>('')
  const [subscriptionPausedStatus, setSubscriptionPausedStatus] = useState<string>('')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  // A second click lands before the disabled button re-renders; this drops it.
  const submitting = useRef(false)
  // Clears the success message; cancelled on reset and on the next submit so it can't end a later status.
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSuccessTimer = () => {
    if (successTimer.current) clearTimeout(successTimer.current)
    successTimer.current = null
  }

  useEffect(() => clearSuccessTimer, [])
  const [errorMessage, setErrorMessage] = useState('')
  const [note, setNote] = useState('')

  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()
  const { user } = useAuth()

  // Effect to extract booking ID from Front context
  useEffect(() => {
    if (frontContext) {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext])

  // Effect to set default note prefill
  useEffect(() => {
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `Note added by ${username} in Front.`
      setNote(notePrefill)
    }
  }, [user])

  const resetForm = () => {
    if (submitting.current) return

    clearSuccessTimer()
    setFormStatus('idle')
    setErrorMessage('')
    if (selectedAction === 'record-financial-compensation') {
      setFinancialCompensation('')
    } else if (selectedAction === 'confirm-subscription-paused') {
      setSubscriptionPausedStatus('')
    }
    // Note is reset with the user prefill
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `Note added by ${username} in Front.`
      setNote(notePrefill)
    } else {
      setNote('')
    }
  }

  const handleActionChange = (e: JSX.TargetedEvent<HTMLSelectElement>) => {
    if (submitting.current) return

    setSelectedAction(e.currentTarget.value as FormAction)
    resetForm()
  }

  const handleSubmit = async (e: JSX.TargetedEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (submitting.current) return

    if (!bookingId) {
      setErrorMessage('Booking ID is required')
      setFormStatus('error')
      return
    }

    if (selectedAction === 'record-financial-compensation') {
      if (financialCompensation === '' || financialCompensation === null) {
        setErrorMessage('Financial Compensation amount is required')
        setFormStatus('error')
        return
      }

      submitting.current = true
      clearSuccessTimer()
      setFormStatus('loading')
      setErrorMessage('')

      const body: FinancialCompensationBody = {
        'financial-compensation': Number(financialCompensation),
        note,
      }

      try {
        const response = await fetch(
          `${config.apiBaseUrl}/bookings/${bookingId.trim()}/record-financial-compensation`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          },
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to record financial compensation.')
        }

        setFormStatus('success')
        successTimer.current = setTimeout(() => {
          successTimer.current = null
          setFormStatus('idle')
        }, 3000)
      } catch (error) {
        setFormStatus('error')
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.')
      } finally {
        submitting.current = false
      }
    } else if (selectedAction === 'confirm-subscription-paused') {
      if (!subscriptionPausedStatus) {
        setErrorMessage('Subscription Paused status is required')
        setFormStatus('error')
        return
      }
      if (!note) {
        setErrorMessage('Note is required')
        setFormStatus('error')
        return
      }

      submitting.current = true
      clearSuccessTimer()
      setFormStatus('loading')
      setErrorMessage('')

      const body: ConfirmSubscriptionPausedBody = {
        'subscription-paused': subscriptionPausedStatus,
        note,
      }

      try {
        const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/confirm-subscription-paused`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to confirm subscription paused status.')
        }

        setFormStatus('success')
        successTimer.current = setTimeout(() => {
          successTimer.current = null
          setFormStatus('idle')
        }, 3000)
      } catch (error) {
        setFormStatus('error')
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.')
      } finally {
        submitting.current = false
      }
    }

    // Add more actions here in the future
  }

  const subscriptionPausedOptions = [
    { value: 'Paused - Chargeback Investigation', label: 'Paused - Chargeback Investigation' },
    { value: 'Paused - Sensitive Case Investigation', label: 'Paused - Sensitive Case Investigation' },
    { value: 'Resumed - Monitoring Required', label: 'Resumed - Monitoring Required' },
    { value: 'Resumed', label: 'Resumed' },
    { value: 'Booking Lost', label: 'Booking Lost' },
    { value: 'Booking Lost - EFW', label: 'Booking Lost - EFW' },
    { value: 'Booking Lost - Chargeback received', label: 'Booking Lost - Chargeback received' },
  ]

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">Customer Service</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Customer Service Actions</h1>

      {isFrontApp && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-700">
            Connected to Front conversation. Booking IDs will be automatically detected when available.
          </p>
        </div>
      )}

      <div className="mb-6">
        <label
          htmlFor="bookingId"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Booking ID <span className="text-red-500">*</span>
        </label>
        <input
          id="bookingId"
          type="text"
          value={bookingId}
          onChange={e => setBookingId((e.target as HTMLInputElement).value)}
          disabled={formStatus === 'loading'}
          className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
          placeholder="Enter Booking ID"
          required
        />
        {formStatus === 'error' && !bookingId && <p className="mt-1 text-sm text-red-600">Booking ID is required</p>}
        {bookingId && isFrontApp && (
          <p className="mt-1 text-xs text-gray-500">Booking ID auto-detected from Front conversation</p>
        )}
      </div>

      <div className="mb-6">
        <label
          htmlFor="action"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Select Action
        </label>
        <select
          id="action"
          value={selectedAction}
          onChange={handleActionChange}
          disabled={formStatus === 'loading'}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="record-financial-compensation">Record Financial Compensation</option>
          <option value="confirm-subscription-paused">Confirm Subscription Paused</option>
          {/* Future actions can be added here */}
        </select>
      </div>

      <form onSubmit={handleSubmit}>
        {selectedAction === 'record-financial-compensation' && (
          <div className="space-y-4 mb-4">
            <div>
              <label
                htmlFor="financialCompensation"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Financial Compensation Amount ($) <span className="text-red-500">*</span>
              </label>
              <input
                id="financialCompensation"
                type="number"
                value={financialCompensation}
                onChange={e => setFinancialCompensation(parseFloat((e.target as HTMLInputElement).value) || '')}
                className={`w-full px-3 py-2 border ${formStatus === 'error' && !financialCompensation ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                placeholder="e.g., 50.00"
                required
                step="0.01"
              />
              {formStatus === 'error' && (financialCompensation === '' || financialCompensation === null) && (
                <p className="mt-1 text-sm text-red-600">Financial Compensation amount is required</p>
              )}
            </div>
          </div>
        )}

        {selectedAction === 'confirm-subscription-paused' && (
          <div className="space-y-4 mb-4">
            <div>
              <label
                htmlFor="subscriptionPausedStatus"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Subscription Paused Status <span className="text-red-500">*</span>
              </label>
              <select
                id="subscriptionPausedStatus"
                value={subscriptionPausedStatus}
                onChange={e => setSubscriptionPausedStatus((e.target as HTMLSelectElement).value)}
                className={`w-full px-3 py-2 border ${formStatus === 'error' && !subscriptionPausedStatus ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                required
              >
                <option value="">Select status...</option>
                {subscriptionPausedOptions.map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              {formStatus === 'error' && !subscriptionPausedStatus && (
                <p className="mt-1 text-sm text-red-600">Subscription Paused status is required</p>
              )}
            </div>
          </div>
        )}

        {/* Note field, always shown if an action is selected that requires it */}
        {(selectedAction === 'record-financial-compensation' || selectedAction === 'confirm-subscription-paused') && (
          <div className="mb-6">
            <label
              htmlFor="note"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Note <span className="text-red-500">*</span>
            </label>
            <textarea
              id="note"
              value={note}
              onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              rows={4}
              placeholder="Enter notes..."
              required
            />
            {formStatus === 'error' && !note && <p className="mt-1 text-sm text-red-600">Note is required</p>}
          </div>
        )}

        {formStatus === 'success' && (
          <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-600 font-medium">
              {selectedAction === 'record-financial-compensation' &&
                `Financial compensation successfully recorded for booking ${bookingId}!`}
              {selectedAction === 'confirm-subscription-paused' &&
                `Subscription paused status successfully confirmed for booking ${bookingId}!`}
            </p>
          </div>
        )}

        {formStatus === 'error' && errorMessage && (
          <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 font-medium">Error: {errorMessage}</p>
          </div>
        )}

        <div className="mt-6">
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={resetForm}
              disabled={formStatus === 'loading'}
              className="w-1/3 px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={formStatus === 'loading' || formStatus === 'success'}
              className="w-2/3 px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading' ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
